import type { EconomicBillingStatus } from './model';

export type EconomicMeterSnapshot = Pick<EconomicBillingStatus,
  'epochId' | 'tier' | 'remainingPercent' | 'cycleReference' | 'cycleEndAtMs' | 'scheduledChange'>
  & { credits: Pick<EconomicBillingStatus['credits'], 'availableEur'> };
const key = (uid: string) => `juris.economic-meter.v1:${uid}`;
export function clearEconomicMeterCache(uid: string) {
  try { localStorage.removeItem(key(uid)); } catch { /* Optional presentation cache. */ }
}
export function readEconomicMeterCache(uid: string, now = Date.now()): EconomicMeterSnapshot | null {
  try {
    const row = JSON.parse(localStorage.getItem(key(uid)) || 'null');
    const s = row?.snapshot;
    if (row?.version !== 1 || row.uid !== uid || !Number.isSafeInteger(row.savedAtMs) || row.savedAtMs > now
      || !s || !['free', 'standard', 'professional', 'premium'].includes(s.tier)
      || typeof s.epochId !== 'string' || !s.epochId || typeof s.cycleReference !== 'string' || !s.cycleReference
      || !Number.isSafeInteger(s.cycleEndAtMs) || s.cycleEndAtMs <= now
      || !Number.isInteger(s.remainingPercent) || s.remainingPercent < 0 || s.remainingPercent > 100
      || typeof s.credits?.availableEur !== 'string' || !/^\d+(?:\.\d+)?$/.test(s.credits.availableEur)
      || !Number.isFinite(Number(s.credits.availableEur))) return null;
    const change = s.scheduledChange;
    if (change && (!['Cambia', 'Finaliza'].includes(change.label)
      || !Number.isSafeInteger(change.effectiveAtMs) || change.effectiveAtMs <= now
      || !['free', 'standard', 'professional', 'premium'].includes(change.targetTier)
      || typeof change.targetName !== 'string' || change.targetName.length > 80)) return null;
    // Only presentation fields are returned; cached activation/permissions are never restored.
    return { epochId: s.epochId, tier: s.tier, remainingPercent: s.remainingPercent,
      cycleReference: s.cycleReference, cycleEndAtMs: s.cycleEndAtMs,
      credits: { availableEur: s.credits.availableEur }, ...(change ? { scheduledChange: change } : {}) };
  } catch { return null; }
}
export function saveEconomicMeterCache(uid: string, status: EconomicBillingStatus, now = Date.now()) {
  if (status.tier === 'admin' || !status.cycleEndAtMs || status.cycleEndAtMs <= now) {
    clearEconomicMeterCache(uid); return;
  }
  const snapshot = { epochId: status.epochId, tier: status.tier, remainingPercent: status.remainingPercent,
    cycleReference: status.cycleReference, cycleEndAtMs: status.cycleEndAtMs,
    credits: { availableEur: status.credits.availableEur }, ...(status.scheduledChange ? { scheduledChange: status.scheduledChange } : {}) };
  try { localStorage.setItem(key(uid), JSON.stringify({ version: 1, uid, savedAtMs: now, snapshot })); }
  catch { /* Storage denied/full does not affect product or server admission. */ }
}
